import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../auth/auth-context";
import { useSignOut } from "../auth/useSignOut";

export function UserMenu(): ReactNode {
  const { session } = useAuth();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const user = session?.info.user;
  const initial = (user?.name ?? "?").trim().charAt(0).toUpperCase();

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen((value) => !value);
        }}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-sm font-semibold text-on-accent"
      >
        <span className="sr-only">Account menu for {user?.name ?? "your account"}</span>
        <span aria-hidden="true">{initial}</span>
      </button>

      {open && (
        <ul
          role="menu"
          aria-label="Account"
          className="app-shadow absolute right-0 z-20 mt-2 w-56 rounded-lg border border-line bg-raised py-1 text-sm"
        >
          <li className="border-b border-line px-3 py-2 text-muted">{user?.email ?? user?.name}</li>
          <li role="none">
            <button
              type="button"
              role="menuitem"
              className="w-full px-3 py-2 text-left hover:bg-hover"
              onClick={() => {
                setOpen(false);
                void navigate("/settings");
              }}
            >
              Settings
            </button>
          </li>
          <li role="none">
            <button
              type="button"
              role="menuitem"
              className="w-full px-3 py-2 text-left hover:bg-hover"
              onClick={() => {
                setOpen(false);
                void signOut();
              }}
            >
              Sign out
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}
